import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Loader2, FileText, ListChecks, ShieldAlert, Lock,
  Sparkles, Send, MessagesSquare,
} from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useDataLabel } from '@/lib/dataI18n';
import { toast } from 'sonner';
import { NewEvaluationDialog } from '@/components/admin/NewEvaluationDialog';
import ResponseViewer from '@/components/admin/ResponseViewer';
import { CustomizationDialog } from '@/components/admin/CustomizationDialog';
import { CustomVersionsBadge } from '@/components/admin/CustomVersionsBadge';
import { EvaluationLifecyclePanel } from '@/components/admin/EvaluationLifecyclePanel';
import { EvaluationMetadataCard } from '@/components/library/EvaluationMetadataCard';
import { DuplicateEvaluationButton } from '@/components/library/DuplicateEvaluationButton';
import { LifecyclePill } from '@/components/studio/LifecyclePill';
import { DifficultyPicker, difficultyLabel } from '@/components/studio/DifficultyPicker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApp } from '@/context/AppContext';
import { Settings2 } from 'lucide-react';

/**
 * Read-only questionnaire detail view. Renders every section + question +
 * answer option from the currently-published version (or the latest draft
 * if none published yet). Tagged questions surface visibility badges:
 *   - voluntary         → "Voluntary"
 *   - voluntary_hr_only → "HR only" (visible to HR roles in response views)
 *   - anonymous         → "Anonymous" (respondent identity hidden from admins)
 */
export default function AdminQuestionnaireDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const [params] = useSearchParams();
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  const tx = (es, en) => (isEN && en ? en : (es || en || ''));
  const filterUserId = params.get('user') || null;
  const [showResponses, setShowResponses] = useState(false);

  const [loading, setLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [version, setVersion] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customPreselectCompany, setCustomPreselectCompany] = useState(null);
  const { user } = useApp();
  const canCustomize = ['hm_admin', 'hm_advisor'].includes(user?.role);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await adminApi.getQuestionnaire(id);
        if (!alive) return;
        setQuestionnaire(data.questionnaire);
        // Prefer the published (current) version; fall back to the most recent
        const published = (data.versions || []).find(
          (v) => v.id === data.questionnaire?.current_version_id,
        );
        setVersion(published || data.versions?.[data.versions.length - 1] || null);
      } catch (e) {
        toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, t]);

  // Auto-open the assign dialog when arriving with ?assign=1 (suite console
  // "Assign questionnaire" button)
  useEffect(() => {
    if (params.get('assign') === '1' && !loading && questionnaire) {
      setAssignOpen(true);
    }
  }, [params, loading, questionnaire]);

  if (loading) {
    return (
      <>
        <Topbar
          title={t('pages.questionnaireDetail.loading')}
          subtitle={t('pages.questionnaireDetail.subtitle')}
        />
        <div className="p-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.questionnaireDetail.loading')}
        </div>
      </>
    );
  }

  if (!questionnaire) {
    return (
      <>
        <Topbar title={t('pages.questionnaireDetail.notFoundTitle')} />
        <div className="p-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            {t('pages.questionnaireDetail.notFoundBody')}
          </p>
          <Button onClick={() => navigate('/admin/questionnaires')} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" /> {t('pages.questionnaireDetail.back')}
          </Button>
        </div>
      </>
    );
  }

  const sections = version?.sections || [];
  const questions = version?.questions || [];
  const totalQs = questions.length;

  return (
    <>
      <Topbar
        title={tData(questionnaire.name)}
        subtitle={t('pages.questionnaireDetail.subtitle')}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-qnr-detail-page">
        {/* Back + actions row */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/questionnaires')}
            className="gap-1.5"
            data-testid="qnr-detail-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> {t('pages.questionnaireDetail.back')}
          </Button>
          <div className="flex items-center gap-2">
            <CustomVersionsBadge
              questionnaireId={id}
              visible={canCustomize}
              onEdit={(v) => {
                setCustomPreselectCompany(v.client_id);
                setCustomOpen(true);
              }}
            />
            {canCustomize && (
              <Button
                data-testid="qnr-detail-customize-btn"
                onClick={() => { setCustomPreselectCompany(null); setCustomOpen(true); }}
                variant="outline"
                className="gap-1.5"
              >
                <Settings2 className="h-4 w-4" />
                {t('common.qcustom.customizeForClient')}
              </Button>
            )}
            <Button
              data-testid="qnr-detail-assign-btn"
              onClick={() => setAssignOpen(true)}
              className="bg-gradient-primary gap-1.5"
            >
              <Send className="h-4 w-4" /> {t('pages.questionnaireDetail.assign')}
            </Button>
            {canCustomize && (
              <DuplicateEvaluationButton
                questionnaireId={id}
                variant="outline"
                size="default"
                testId="qnr-detail-duplicate-btn"
              />
            )}
          </div>
        </div>

        {canCustomize && (
          <CustomizationDialog
            open={customOpen}
            onClose={() => { setCustomOpen(false); setCustomPreselectCompany(null); }}
            questionnaire={questionnaire}
            preselectCompanyId={customPreselectCompany}
          />
        )}

        {/* Library MVP — lifecycle status pill + role-aware transition CTAs. */}
        <EvaluationLifecyclePanel
          evaluation={questionnaire}
          onTransitioned={(next) => setQuestionnaire(next)}
        />

        {/* Sprint A — Catalog metadata (objective/instructions/vocab fields). */}
        {canCustomize && (
          <EvaluationMetadataCard
            questionnaire={questionnaire}
            onSaved={(updated) => setQuestionnaire(updated)}
          />
        )}

        {/* Phase 1 — Studio Inspector (Difficulty + Estimated Minutes + Passing Score). */}
        {canCustomize && (
          <StudioInspectorCard
            questionnaire={questionnaire}
            onSaved={(updated) => setQuestionnaire((prev) => ({ ...prev, ...updated }))}
          />
        )}

        {/* Submitted responses (lazy-mount) */}
        <Card className="p-5 shadow-card" data-testid="qnr-detail-responses-card">
          <button
            type="button"
            onClick={() => setShowResponses((v) => !v)}
            className="w-full flex items-center justify-between gap-3 text-left"
            data-testid="qnr-detail-responses-toggle"
          >
            <div className="flex items-center gap-2">
              <MessagesSquare className="h-5 w-5 text-primary" />
              <div>
                <h2 className="font-display text-lg font-bold tracking-tight">
                  {filterUserId
                    ? t('pages.questionnaireDetail.responsesForUser')
                    : t('pages.questionnaireDetail.responsesTitle')}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {t('pages.questionnaireDetail.responsesSubtitle')}
                </p>
              </div>
            </div>
            <span
              className="inline-flex items-center justify-center rounded-md px-3 h-8 text-sm font-medium text-foreground/80 hover:bg-muted/60 transition-colors"
              aria-hidden="true"
            >
              {showResponses
                ? t('pages.questionnaireDetail.hideResponses')
                : t('pages.questionnaireDetail.showResponses')}
            </span>
          </button>
          {showResponses && (
            <div className="mt-4">
              <ResponseViewer questionnaireId={questionnaire.id} userId={filterUserId} />
            </div>
          )}
        </Card>


        {/* Header card */}
        <Card className="p-6 lg:p-8 shadow-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <code className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                  {questionnaire.code}
                </code>
                <LifecyclePill
                  assessment={questionnaire}
                  userRole={user?.role}
                  userId={user?.id}
                  onUpdated={(next) => setQuestionnaire((prev) => ({ ...prev, ...next }))}
                />
                {(questionnaire.suite_codes || []).map((s) => (
                  <Badge key={s} variant="outline" className="text-xs">
                    {tData(s)}
                  </Badge>
                ))}
                {version && (
                  <Badge variant="outline" className="text-xs gap-1">
                    <Sparkles className="h-3 w-3" /> v{version.version_number}
                  </Badge>
                )}
              </div>
              <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mb-2">
                {tData(questionnaire.name)}
              </h1>
              <p className="text-sm text-muted-foreground max-w-3xl leading-relaxed">
                {tData(questionnaire.description)}
              </p>
              {(questionnaire.tags || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="qnr-detail-tags">
                  {questionnaire.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className={`text-xs ${tag === 'imported_from_resource_center'
                        ? 'border-primary/40 bg-primary/5 text-primary'
                        : ''}`}
                      data-testid={`qnr-detail-tag-${tag}`}
                    >
                      {tag === 'imported_from_resource_center'
                        ? t('library.tags.importedFromResourceCenter', { defaultValue: 'Imported from Resource Center' })
                        : tag}
                    </Badge>
                  ))}
                </div>
              )}
              {questionnaire.imported_from_resource_title && (
                <p className="mt-1 text-xs text-muted-foreground italic" data-testid="qnr-detail-import-source">
                  {t('library.tags.importSource', { defaultValue: 'Imported from' })}: {questionnaire.imported_from_resource_title}
                </p>
              )}
              {(questionnaire.objective || questionnaire.instructions) && (
                <div className="mt-3 space-y-2 max-w-3xl" data-testid="qnr-detail-context">
                  {questionnaire.objective && (
                    <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm leading-relaxed">
                      <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-0.5">
                        {t('library.metadata.objective.label', { defaultValue: 'Objective' })}
                      </p>
                      <p className="text-foreground/80">{questionnaire.objective}</p>
                    </div>
                  )}
                  {questionnaire.instructions && (
                    <div className="rounded-lg border border-accent/20 bg-accent/5 px-3 py-2 text-sm leading-relaxed">
                      <p className="text-xs uppercase tracking-wider font-semibold text-accent mb-0.5">
                        {t('library.metadata.instructions.label', { defaultValue: 'Instructions' })}
                      </p>
                      <p className="text-foreground/80">{questionnaire.instructions}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3" data-testid="qnr-detail-summary">
              <SummaryStat
                icon={ListChecks}
                label={t('pages.questionnaireDetail.sectionsCount')}
                value={sections.length}
              />
              <SummaryStat
                icon={FileText}
                label={t('pages.questionnaireDetail.questionsCount')}
                value={totalQs}
              />
            </div>
          </div>
        </Card>

        {/* Sections + questions */}
        {sections.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-sm text-muted-foreground">
              {t('pages.questionnaireDetail.emptyVersion')}
            </p>
          </Card>
        ) : (
          sections
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((s, sIdx) => {
              const sectionQs = questions.filter((q) => q.section_id === s.id);
              const intro = tx(s.description, s.description_en);
              return (
                <Card
                  key={s.id}
                  className="p-6 lg:p-7 shadow-card"
                  data-testid={`qnr-detail-section-${sIdx}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div>
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                        {t('pages.questionnaireDetail.sectionLabel', {
                          n: sIdx + 1,
                          defaultValue: 'Section {{n}}',
                        })}
                      </p>
                      <h2 className="font-display text-xl font-bold tracking-tight">
                        {tx(s.name, s.name_en)}
                      </h2>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {t('pages.questionnaireDetail.qCount', { n: sectionQs.length })}
                    </Badge>
                  </div>

                  {intro && (
                    <div className="mb-5 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-foreground/80 leading-relaxed">
                      <p className="text-xs uppercase tracking-wider font-semibold text-accent mb-1">
                        {t('pages.questionnaireDetail.voluntaryIntro')}
                      </p>
                      {intro}
                    </div>
                  )}

                  <ol className="space-y-4">
                    {sectionQs.map((q, qIdx) => (
                      <QuestionRow
                        key={q.id}
                        q={q}
                        index={qIdx + 1}
                        tx={tx}
                        t={t}
                      />
                    ))}
                  </ol>
                </Card>
              );
            })
        )}
      </div>

      {/* Pre-selected assignment dialog */}
      <NewEvaluationDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        defaultQuestionnaireId={questionnaire.id}
        defaultQuestionnaireLabel={questionnaire.name}
        defaultSuiteCode={(questionnaire.suite_codes || [])[0]}
        onCreated={() => toast.success(t('pages.questionnaireDetail.assignedToast'))}
      />
    </>
  );
}

function SummaryStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-muted/40 px-4 py-3 flex items-center gap-3">
      <Icon className="h-5 w-5 text-primary" />
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          {label}
        </p>
        <p className="font-display text-2xl font-bold tabular-nums">{value}</p>
      </div>
    </div>
  );
}

function QuestionRow({ q, index, tx, t }) {
  const tag = q.dimension_tag;
  const isHrOnly = tag === 'voluntary_hr_only';
  const isAnonymous = tag === 'anonymous';
  const isVoluntary = tag === 'voluntary' || isHrOnly || !q.required;

  const typeLabel = {
    single_choice: t('pages.questionnaireDetail.typeSingleChoice'),
    multi_choice: t('pages.questionnaireDetail.typeMultiChoice'),
    likert_5: t('pages.questionnaireDetail.typeLikert'),
    scale_1_10: t('pages.questionnaireDetail.typeScale'),
    text: t('pages.questionnaireDetail.typeText'),
    long_text: t('pages.questionnaireDetail.typeLongText'),
  }[q.type] || q.type;

  return (
    <li
      data-testid={`qnr-detail-question-${q.id}`}
      className="rounded-xl border border-border/70 p-4 bg-card"
    >
      <div className="flex items-start gap-3 mb-2 flex-wrap">
        <span className="h-7 w-7 shrink-0 rounded-md bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {index}
        </span>
        <p className="flex-1 min-w-0 text-sm leading-relaxed font-medium">
          {tx(q.prompt, q.prompt_en)}
        </p>
      </div>

      <div className="ml-10 flex items-center gap-1.5 flex-wrap">
        <Badge variant="outline" className="text-xs">{typeLabel}</Badge>
        {isVoluntary && !isHrOnly && (
          <Badge
            data-testid="qnr-detail-tag-voluntary"
            variant="outline"
            className="text-xs bg-info/10 text-info border-info/20 gap-1"
          >
            <Sparkles className="h-3 w-3" /> {t('pages.questionnaireDetail.tagVoluntary')}
          </Badge>
        )}
        {isHrOnly && (
          <Badge
            data-testid="qnr-detail-tag-hr-only"
            variant="outline"
            className="text-xs bg-warning/10 text-warning border-warning/20 gap-1"
          >
            <Lock className="h-3 w-3" /> {t('pages.questionnaireDetail.tagHrOnly')}
          </Badge>
        )}
        {isAnonymous && (
          <Badge
            data-testid="qnr-detail-tag-anonymous"
            variant="outline"
            className="text-xs bg-accent/10 text-accent border-accent/20 gap-1"
          >
            <ShieldAlert className="h-3 w-3" /> {t('pages.questionnaireDetail.tagAnonymous')}
          </Badge>
        )}
      </div>

      {q.options && q.options.length > 0 && (
        <>
          <Separator className="my-3 ml-10" />
          <ul className="ml-10 space-y-1.5">
            {q.options.map((o, oi) => (
              <li
                key={o.id || oi}
                className="text-xs text-foreground/80 flex items-center gap-2"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary/50" />
                <span className="flex-1">{tx(o.label, o.label_en)}</span>
                {typeof o.score === 'number' && (
                  <span className="text-muted-foreground tabular-nums">
                    {t('pages.questionnaireDetail.scoreLabel', { score: o.score })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </li>
  );
}


function StudioInspectorCard({ questionnaire, onSaved }) {
  const { t } = useTranslation();
  const [difficulty, setDifficulty] = useState(questionnaire.difficulty ?? null);
  const [estimatedMinutes, setEstimatedMinutes] = useState(questionnaire.estimated_minutes ?? '');
  const [passingScore, setPassingScore] = useState(questionnaire.passing_score ?? '');
  const [saving, setSaving] = useState(false);

  // Refresh local state if parent updates.
  useEffect(() => {
    setDifficulty(questionnaire.difficulty ?? null);
    setEstimatedMinutes(questionnaire.estimated_minutes ?? '');
    setPassingScore(questionnaire.passing_score ?? '');
  }, [questionnaire.id, questionnaire.difficulty, questionnaire.estimated_minutes, questionnaire.passing_score]);

  const dirty = (
    (difficulty ?? null) !== (questionnaire.difficulty ?? null) ||
    String(estimatedMinutes) !== String(questionnaire.estimated_minutes ?? '') ||
    String(passingScore) !== String(questionnaire.passing_score ?? '')
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {
        difficulty: difficulty ?? null,
        estimated_minutes: estimatedMinutes === '' ? null : Number(estimatedMinutes),
        passing_score: passingScore === '' ? null : Number(passingScore),
      };
      const updated = await adminApi.updateQuestionnaireMeta(questionnaire.id, patch);
      toast.success(t('studio.detail.savedToast', { defaultValue: 'Studio metadata saved.' }));
      if (onSaved) onSaved(updated);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 shadow-card" data-testid="studio-inspector-card">
      <h2 className="font-display text-lg font-bold tracking-tight mb-1">
        {t('studio.detail.lifecycleLabel', { defaultValue: 'Studio metadata' })}
      </h2>
      <p className="text-xs text-muted-foreground mb-4">
        {t('studio.detail.subtitle', { defaultValue: 'Asset-type fields used by Assessment Studio. Saved directly on the assessment.' })}
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
            {t('studio.detail.difficultyLabel', { defaultValue: 'Difficulty' })}
          </Label>
          <div className="mt-2">
            <DifficultyPicker value={difficulty} onChange={setDifficulty} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {difficulty ? difficultyLabel(t, difficulty) : '—'}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="studio-est-min" className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              {t('studio.detail.estimatedTimeLabel', { defaultValue: 'Estimated minutes' })}
            </Label>
            <Input
              id="studio-est-min"
              data-testid="studio-estimated-minutes"
              type="number"
              min="0"
              value={estimatedMinutes}
              onChange={(e) => setEstimatedMinutes(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="studio-pass-score" className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              {t('studio.detail.passingScoreLabel', { defaultValue: 'Passing score (%)' })}
            </Label>
            <Input
              id="studio-pass-score"
              data-testid="studio-passing-score"
              type="number"
              min="0"
              max="100"
              value={passingScore}
              onChange={(e) => setPassingScore(e.target.value)}
              className="mt-1.5"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid="studio-inspector-save"
          size="sm"
        >
          {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          {t('common.save', { defaultValue: 'Save' })}
        </Button>
      </div>
    </Card>
  );
}
